<script lang="ts" setup>
import type { CatalogPageRecord, LayoutRecord, ProductGroupPromoRecord } from "~~/server/graphql/datocms/generated";
import type { PlanktonCategoryWithSlugs } from "~~/server/plankton";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";

const { category, productGroupPromo } = defineProps<{
  layout: LayoutRecord;
  cmsData: CatalogPageRecord;
  category?: PlanktonCategoryWithSlugs | null | undefined;
  productGroupPromo?: ProductGroupPromoRecord | null | undefined;
}>();

const { loggedIn } = useUserSession();
const showProductSearchDebugInfo = useShowProductSearchDebugInfo();

const { status, error, catalog, searchState, catalogPaged } = useCatalog(
  {
    initialize: {
      searchState: false,
      catalog: false,
      ssr: !loggedIn.value,
      scrollIntoViewThreshold: 500,
    },
    params: {
      category: category,
    },
  },
);

const isInvalidSearchTerm = computed(() => searchState.value.term?.length && searchState.value.term?.length < 3);

const breakpoints = useBreakpoints(breakpointsTailwind);
const productGroupPromoPosition = ref<number>(Number.MAX_SAFE_INTEGER);

const productCount = computed(() => productGroupPromo ? (catalog.value.length + 1) : catalog.value.length);

// only load prices when authenticated
const { prices, isPricesLoading } = usePricing(catalog, { loggedIn: loggedIn.value });

function updateProductGroupPromoPosition() {
  if (!productGroupPromo) {
    return;
  }

  if (breakpoints.active().value === "xl" || breakpoints.active().value === "2xl") {
    productGroupPromoPosition.value = 4;
  }
  else if (breakpoints.active().value === "md") {
    productGroupPromoPosition.value = 2;
  }
  else {
    productGroupPromoPosition.value = 3;
  }
}

watch(() => breakpoints.active().value, () => {
  updateProductGroupPromoPosition();
});

onMounted(() => {
  updateProductGroupPromoPosition();
});

function getDebugInfo(productId: number | undefined) {
  if (!productId) {
    return "";
  }

  const catalogPagedItem = catalogPaged.value[searchState.value.page]?.find((item) => item.productId === productId);
  return catalogPagedItem;
}
</script>

<template>
  <div>
    <div v-if="status === 'error' || status === 'success'" class="gap-sm mb-md flex flex-col">
      <FeedbackMessage v-if="error && isInvalidSearchTerm" :msg="cmsData.messageInvalidSearchTerm" type="warning" />
      <FeedbackMessage v-else-if="error && error.statusCode !== 400" :msg="error.message" type="fail" />
      <FeedbackMessage v-else-if="!catalog.length" :msg="cmsData.messageNoItems" type="warning" />
    </div>

    <div v-if="catalog.length" class="gap-xl lg:gap-4xl flex flex-col">
      <div class="gap-xs lg:gap-md grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        <div v-for="pos of productCount" :key="pos" :class="{ 'col-span-2': productGroupPromo && pos === productGroupPromoPosition }">
          <ProductCardItem
            v-if="!productGroupPromo || pos !== productGroupPromoPosition"
            :layout="layout"
            :cms-data="cmsData"
            :product="catalog.at(pos - (pos > productGroupPromoPosition ? 2 : 1))"
            :prices="prices"
            :is-prices-loading="isPricesLoading"
            :overpack-popper="{ placement: 'bottom' }"
          />

          <div v-if="showProductSearchDebugInfo && (!productGroupPromo || pos !== productGroupPromoPosition)">
            <code style="font-size: 11px;">
              {{ getDebugInfo(catalog.at(pos - (pos > productGroupPromoPosition ? 2 : 1))?.id) }}
            </code>
          </div>

          <ProductCardPromo
            v-if="productGroupPromo && pos === productGroupPromoPosition && category"
            :data="productGroupPromo as ProductGroupPromoRecord"
            :category-name="category.internalName"
            :category-code="category.code"
          />
        </div>
      </div>

      <ProductSearchPagination class="mx-auto" />

      <ProductCategorySeoOptimizedText v-if="category" :category="category" />
    </div>
  </div>
</template>

<style scoped>

</style>
